import t from 'react-translate';
import { AngularContext } from 'react-app';
import { useContext, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { SingleMentorRequestStatus, UpdateRequestHandlerType } from 'redux/schemas/models/mentor-request';
import { getMentorshipProgramEnrollmentById } from 'redux/selectors/mentorship-program-enrollments';

import { useMentorRequestContext } from '../mentor-request-context';
import useMentorAvailability from 'athena/hooks/use-mentor-availability';
import MentoringProgramContext from 'athena/components/mentoring-program/context';

import { Button } from 'react-bootstrap';
import ReachingYourLimitModal from './reaching-your-limit-modal';

import { config } from '@config/pendo.config.json';

const MentorRequestApprovalActions = () => {
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const limitModalFlag = !!$state.params.showLimitModal;
  const [showLimitModal, setShowLimitModal] = useState(limitModalFlag);
  const { approveRequest, rejectRequest } = useMentorRequestContext();
  const [CurrentUserManager] = injectServices(['CurrentUserManager']);

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { mentorshipProgramEnrollment: enrollmentId } = mentoringProgram || {};
  const enrollment = useSelector(getMentorshipProgramEnrollmentById(enrollmentId));
  const { availability, totalCapacity } = useMentorAvailability(enrollment);

  const actions = useMemo(() => ({
    [SingleMentorRequestStatus.APPROVED]: approveRequest,
    [SingleMentorRequestStatus.REJECTED]: rejectRequest,
  }), [approveRequest, rejectRequest]);

  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTOR_REQUEST_MODAL.MENTEE_VIEW;
  const handleAction = (status: SingleMentorRequestStatus) => {
    const action: () => UpdateRequestHandlerType = actions[status];
    const promise = action();
    promise.then(() => {
      CurrentUserManager.user.updateFromReact({
        updateInvitationsAndRequests: true,
      });
    });
  };

  const handleApprove = () => {
    if (availability <= 1) {
      setShowLimitModal(true);
      return;
    }
    handleAction(SingleMentorRequestStatus.APPROVED);
  };

  const handleReject = () => {
    handleAction(SingleMentorRequestStatus.REJECTED);
  };

  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.mentorRequest.modal.mentor;

  useEffect(() => {
    setShowLimitModal(!!$state.params.showLimitModal);
  }, [$state.params.showLimitModal]);

  return (
    <>
      <div className='d-flex justify-content-between mt-4'>
        <Button
          size='lg'
          className='mr-2'
          variant='primary'
          onClick={handleApprove}
          data-qa={dataQATag.acceptButton}
        >
          {translationBase.ACCEPT_REQUEST()}
        </Button>
        <Button
          size='lg'
          variant='secondary'
          onClick={handleReject}
          data-qa={dataQATag.rejectButton}
        >
          {translationBase.REJECT_REQUEST()}
        </Button>
      </div>
      {showLimitModal && (
        <ReachingYourLimitModal
          limit={totalCapacity}
          onCancel={() => {
            setShowLimitModal(false);
          }}
          onContinue={() => {
            setShowLimitModal(false);
            handleAction(SingleMentorRequestStatus.APPROVED);
          }}
        />
      )}
    </>
  );
};

export default MentorRequestApprovalActions;
